import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../layout/Layout';
import { paydlesApi } from '../../services/api';
import { Paydle, PaydleAttempt, LetterStatus, PaydleStatus } from '../../types/paydle';
import { useAuth } from '../../hooks/useAuth';

const LetterTile: React.FC<{ letter: string; status: LetterStatus }> = ({ letter, status }) => {
  const getColor = () => {
    switch (status) {
      case LetterStatus.CORRECT:
        return 'bg-green-500 text-white';
      case LetterStatus.PRESENT:
        return 'bg-yellow-500 text-white';
      case LetterStatus.ABSENT:
        return 'bg-gray-500 text-white';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  return (
    <div
      className={`w-8 h-8 flex items-center justify-center text-sm font-bold ${getColor()} rounded`}
    >
      {letter}
    </div>
  );
};

const AttemptCard: React.FC<{ attempt: PaydleAttempt; index: number }> = ({ attempt, index }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusBadge = (status: PaydleStatus) => {
    switch (status) {
      case PaydleStatus.WON:
        return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">Won</span>;
      case PaydleStatus.LOST:
        return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">Lost</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">In Progress</span>;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-lg shadow-md p-4 mb-3 hover:shadow-lg transition-shadow"
    >
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className="text-lg font-bold text-gray-700">#{index + 1}</div>
          <div>
            <p className="text-sm font-semibold text-gray-800">
              {attempt.userName || 'Anonymous Player'}
            </p>
            <p className="text-xs text-gray-500">
              {attempt.guesses.length} {attempt.guesses.length === 1 ? 'guess' : 'guesses'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(attempt.status)}
          <button className="text-gray-500 hover:text-gray-700">
            {expanded ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="mt-4 space-y-2"
        >
          {attempt.guesses.map((guess, guessIndex) => (
            <div key={guessIndex} className="flex items-center gap-2">
              <span className="text-sm text-gray-500 w-8">#{guessIndex + 1}</span>
              <div className="flex gap-1">
                {guess.word.split('').map((letter, letterIndex) => (
                  <LetterTile
                    key={letterIndex}
                    letter={letter}
                    status={guess.letterStatuses[letterIndex]}
                  />
                ))}
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
};

export const PaydleDashboard: React.FC = () => {
  const { accessToken } = useParams<{ accessToken: string }>();
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [paydle, setPaydle] = useState<Paydle | null>(null);
  const [attempts, setAttempts] = useState<PaydleAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Redirect to sign-up if not authenticated (after auth finishes loading)
  useEffect(() => {
    if (!authLoading && currentUser === null) {
      navigate('/sign-up');
    }
  }, [currentUser, authLoading, navigate]);

  useEffect(() => {
    const loadDashboard = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // If not authenticated after loading, don't try to load dashboard
      if (!currentUser) {
        return;
      }

      if (!accessToken) {
        setError('No access token provided');
        setLoading(false);
        return;
      }

      try {
        // Get Paydle info
        const paydleResponse = await paydlesApi.getByAccessToken(accessToken);
        const paydleData = paydleResponse.data as Paydle;
        setPaydle(paydleData);

        // Get attempts
        const attemptsResponse = await paydlesApi.getAttempts(paydleData.id, accessToken);
        setAttempts(attemptsResponse.data.attempts);

        setLoading(false);
      } catch (err: any) {
        console.error('Error loading dashboard:', err);
        setError(err.response?.data?.error || 'Failed to load dashboard');
        setLoading(false);
      }
    };

    loadDashboard();
  }, [accessToken, currentUser, authLoading]);

  const copyShareLink = () => {
    if (!paydle) return;

    const shareUrl = `${window.location.origin}/paydle/play/${paydle.shareId}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Show loading while auth initializes
  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center bg-gray-50 py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !paydle) {
    return (
      <Layout>
        <div className="flex items-center justify-center bg-gray-50 p-4 py-20">
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg max-w-md">
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p>{error || 'Paydle not found'}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const winRate = paydle.totalAttempts > 0
    ? ((paydle.totalWins / paydle.totalAttempts) * 100).toFixed(1)
    : '0.0';

  return (
    <Layout>
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-6"
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Paydle Dashboard
          </h1>

          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Your Word</h2>
            <div className="flex items-center gap-2">
              {paydle.word.split('').map((letter, index) => (
                <div
                  key={index}
                  className="w-12 h-12 bg-blue-600 text-white flex items-center justify-center text-2xl font-bold rounded"
                >
                  {letter}
                </div>
              ))}
            </div>
            <p className="text-gray-600 mt-2">Price: ${paydle.price.toFixed(2)}</p>
            {paydle.description && (
              <p className="text-gray-600 mt-1 italic">{paydle.description}</p>
            )}
          </div>

          {/* Share Link */}
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">Share Link</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={`${window.location.origin}/paydle/play/${paydle.shareId}`}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded bg-white"
              />
              <button
                onClick={copyShareLink}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold transition-colors"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          <div className="bg-white rounded-lg shadow-md p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{paydle.totalAttempts}</p>
            <p className="text-sm text-gray-600">Total Plays</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{paydle.totalWins}</p>
            <p className="text-sm text-gray-600">Wins</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{paydle.totalLosses}</p>
            <p className="text-sm text-gray-600">Losses</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{winRate}%</p>
            <p className="text-sm text-gray-600">Win Rate</p>
          </div>
        </motion.div>

        {/* Attempts List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">All Attempts</h2>
          {attempts.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              No attempts yet. Share your Paydle to get started!
            </div>
          ) : (
            <div>
              {attempts.map((attempt, index) => (
                <AttemptCard key={attempt.id} attempt={attempt} index={index} />
              ))}
            </div>
          )}
        </motion.div>
        </div>
      </div>
    </Layout>
  );
};
